import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ArrowRight, Download, Gift, Smartphone, QrCode, SkipForward } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/**
 * Shown on Phase 2 (listing mapping) when the host has NO listings in their
 * Atiti OTA pool. Serves three jobs at once:
 *   1. Promote the Atiti OTA app (QR codes for iOS + Android)
 *   2. Nudge users who *do* have listings to hit refresh
 *   3. Offer a "skip for now" escape hatch → straight to billing
 *
 * Once billed, the host can revisit /pms/property/:id/setup and finish
 * the mapping any time (the skip flag is auto-cleared by the backend).
 */

// Store links come from env so real URLs can be swapped without a rebuild.
// Sensible fallbacks (store search) so the QR always resolves to something valid.
const IOS_URL = process.env.REACT_APP_ATITI_IOS_URL
  || "https://apps.apple.com/search?term=atiti%20homestay";
const ANDROID_URL = process.env.REACT_APP_ATITI_ANDROID_URL
  || "https://play.google.com/store/search?q=atiti%20homestay";
const qrSrc = (url) => `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(url)}`;

export default function EmptyPoolCTA({ pid, onSkip, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/pms/refresh-pool");
      if (r.added > 0) toast.success(`Pulled ${r.added} listing${r.added > 1 ? "s" : ""} from Atiti OTA`);
      else toast.info("No listings yet — create one on the Atiti OTA app first");
      await onRefresh?.();
    } catch (e) { toast.error(errMsg(e)); }
    setRefreshing(false);
  };

  const skip = async () => {
    setSkipping(true);
    try {
      await api.post(`/pms/properties/${pid}/setup-complete`, { skip_mapping: true });
      toast.success("Mapping skipped — you can connect listings any time from setup");
      await onSkip?.();
    } catch (e) { toast.error(errMsg(e)); }
    setSkipping(false);
  };

  return (
    <div data-testid="empty-pool-cta" className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-white p-6 md:p-8">
      {/* Decorative flourish */}
      <div aria-hidden className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-indigo-100/50 blur-2xl" />
      <div aria-hidden className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-pink-100/40 blur-2xl" />

      <div className="relative grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 items-start">
        {/* LEFT — messaging + skip / refresh CTAs */}
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-full px-2.5 py-1"><Gift size={11} /> New host bonus</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-3 leading-tight">
            No Atiti OTA listings yet? <br /><span className="brand-pink">Buy PMS — OTA comes free.</span>
          </h2>
          <p className="text-sm text-slate-600 mt-3 max-w-lg">
            Every PMS subscription includes a <b>complimentary listing</b> on the Atiti OTA app —
            zero commission, direct guest bookings, and instant sync back into this workspace.
            Download the Atiti OTA app, create your listing in 2 minutes, then come back here to connect the dots.
          </p>

          <ul className="mt-5 space-y-2 text-[13px] text-slate-700">
            <Bullet>OTA services <b>completely free</b> when bundled with PMS</Bullet>
            <Bullet>Bookings sync into your calendar the moment they arrive</Bullet>
            <Bullet>Come back any time — mapping is not one-shot</Bullet>
          </ul>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button data-testid="empty-pool-refresh-button" onClick={refresh} disabled={refreshing || skipping}
              className="text-xs font-semibold px-4 py-2.5 rounded-full border border-slate-300 text-slate-600 hover:border-slate-500 hover:text-slate-900 bg-white flex items-center gap-2 disabled:opacity-50">
              {refreshing ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} I already have listings
            </button>
            <button data-testid="empty-pool-skip-button" onClick={skip} disabled={skipping || refreshing}
              className="btn-brand text-xs font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60">
              {skipping ? <Loader2 className="animate-spin" size={13} /> : <SkipForward size={13} />} Skip for now, continue to billing <ArrowRight size={13} />
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">You can finish listing mapping later from <b>Setup → Phase 2</b>. Nothing gets locked.</p>
        </div>

        {/* RIGHT — Atiti OTA app promo with QR codes */}
        <div className="bg-white/70 backdrop-blur rounded-2xl border border-slate-200 p-5 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Download size={11} /> Get the Atiti OTA app</p>
          <p className="text-sm font-display font-bold text-slate-900 mt-1">Create your listing in under 2 minutes.</p>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <a data-testid="qr-ios" href={IOS_URL} target="_blank" rel="noopener noreferrer"
              className="group flex flex-col items-center rounded-xl border border-slate-200 bg-white p-3 hover:border-indigo-500 hover:shadow-md transition-all">
              <img src={qrSrc(IOS_URL)} alt="iOS App Store QR" className="w-[120px] h-[120px] rounded-md" loading="lazy" />
              <div className="mt-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 group-hover:brand-pink">
                <Smartphone size={11} /> iOS · App Store
              </div>
            </a>
            <a data-testid="qr-android" href={ANDROID_URL} target="_blank" rel="noopener noreferrer"
              className="group flex flex-col items-center rounded-xl border border-slate-200 bg-white p-3 hover:border-indigo-500 hover:shadow-md transition-all">
              <img src={qrSrc(ANDROID_URL)} alt="Google Play QR" className="w-[120px] h-[120px] rounded-md" loading="lazy" />
              <div className="mt-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 group-hover:brand-pink">
                <Smartphone size={11} /> Android · Play Store
              </div>
            </a>
          </div>
          <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5"><QrCode size={11} className="mt-0.5 shrink-0" /> Scan with your phone camera. Same login as PMS — your properties auto-appear.</p>
        </div>
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
